// JavaScript files are compiled and minified during the build process to the assets/built folder. See available scripts in the package.json file.

// Import CSS (extracted to a separate file by Rollup)
import "../css/index.css";
// Ensure local fonts CSS are part of the bundle
import "../css/fonts.css";

// Minimal boot: only the tiny nav toggle runs immediately
import menuOpen from "./menuOpen";
// Always bundle wiki modules so they’re available on any page
import "./wiki.js";
import "./wiki-markdown.js";
menuOpen();

// Defer non-critical JS
const onReady = (cb) => {
  if (document.readyState === "complete" || document.readyState === "interactive") {
    cb();
  } else {
    document.addEventListener("DOMContentLoaded", cb, { once: true });
  }
};

const whenIdle = (cb) => {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(cb, { timeout: 3000 });
  } else {
    setTimeout(cb, 1);
  }
};

onReady(() => {
  // Load infinite scroll only if a post feed exists and there is a next page
  const hasPostFeed = !!document.querySelector('.gh-postfeed');
  const hasNextLink = !!document.querySelector('link[rel="next"]');
  if (hasPostFeed && hasNextLink) {
    import(/* webpackChunkName: "infiniteScroll" */ "./infiniteScroll").then((m) => m.default && m.default());
  }

  // Load wiki systems on wiki paths or when wiki DOM is present
  const onWikiPath = typeof window !== 'undefined' && /\/wiki(\/|$)/.test(window.location.pathname || '');
  const isWikiPage = !!document.querySelector('.wiki-container');
  const isWikiIndex = !!document.querySelector('.wiki-index-container');
  const hasMarkdownWiki = !!(window.wikiConfig && window.wikiConfig.dataUrl);

  // If we're on /wiki but the template didn't include the wiki markup,
  // inject a minimal index container and config so the JS can render.

  if (onWikiPath && !isWikiPage && !isWikiIndex) {
    const hostMain = document.querySelector('main.gh-main') || document.body;
    const container = document.createElement('div');
    container.className = 'gh-article';
    container.innerHTML = `
      <div class="gh-container">
        <div class="wiki-index-container">
          <header class="wiki-index-header">
            <h1>Wiki Documentation</h1>
            <div class="wiki-index-search">
              <input type="text" id="wiki-index-search" placeholder="Search all wiki pages..." class="wiki-index-search-input">
              <button type="button" id="wiki-index-search-btn" class="wiki-index-search-btn" aria-label="Search"></button>
            </div>
          </header>
          <div class="wiki-index-content">
            <div class="wiki-categories" id="wiki-categories"></div>
            <section class="wiki-recent">
              <h2 class="wiki-section-title">Recent Updates</h2>
              <div class="wiki-recent-grid" id="wiki-recent-grid"></div>
            </section>
            <section class="wiki-all-pages">
              <h2 class="wiki-section-title">All Pages</h2>
              <div class="wiki-pages-list" id="wiki-pages-list"></div>
            </section>
          </div>
        </div>
      </div>`;
    // Try to append within the existing article/main structure if possible
    if (hostMain && hostMain.firstElementChild) {
      hostMain.firstElementChild.appendChild(container);
    } else if (hostMain) {
      hostMain.appendChild(container);
    } else {
      document.body.appendChild(container);
    }

    // Provide a safe default config if the template didn't set one
    window.wikiIndexConfig = window.wikiIndexConfig || {
      baseUrl: '',
      dataUrl: '/assets/built/wiki-data.json',
      fallbackToGhost: false,
      ghostApiUrl: `${window.location.origin}/ghost/api/content/posts/`,
      ghostApiKey: '',
      siteTitle: document.title || '',
      siteDescription: ''
    };
  }

  // Proactively prefetch wiki-data.json so it appears in Network and warms cache
  if (onWikiPath) {
    try {
      const cfg = window.wikiIndexConfig || {};
      const dataUrl = (cfg.dataUrl && typeof cfg.dataUrl === 'string') ? cfg.dataUrl : '/assets/built/wiki-data.json';
      const bust = (dataUrl.includes('?') ? '&' : '?') + 'v=' + Date.now();
      const absolute = dataUrl.startsWith('http') ? dataUrl : (dataUrl.startsWith('/') ? dataUrl : ('/' + dataUrl));
      fetch(absolute + bust, { credentials: 'same-origin' }).catch(() => {});
    } catch {}
  }

  // Modules already bundled above

  // Low-priority enhancements loaded when the browser is idle
  whenIdle(() => {
    import(/* webpackChunkName: "navigation" */ "./navigation.js");
    import(/* webpackChunkName: "commentsStyle" */ "./comments-style.js");

    // Load portal styling only if Ghost Portal is detected
    const maybeLoadPortalStyle = () => {
      const found = document.querySelector(
        '#ghost-portal-root, [id^="ghost-portal"], [id^="gh-portal"], iframe.gh-portal-triggerbtn-iframe, script[src*="portal.min.js"]'
      );
      if (found) {
        import(/* webpackChunkName: "portalStyle" */ "./portal-style.js");
        return true;
      }
      return false;
    };

    if (!maybeLoadPortalStyle()) {
      const observer = new MutationObserver(() => {
        if (maybeLoadPortalStyle()) observer.disconnect();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 15000);
    }

    // Randomize starfield glyph sizes/opacity and assign non-overlapping lanes
    try {
      const GLYPH_MIN = 0.85;
      const GLYPH_MAX = 1.35;
      const OP_MIN = 0.28;
      const OP_MAX = 0.5;
      const glyphs = Array.from(document.querySelectorAll('.glyph-faller'));
      if (glyphs.length) {
        const rand = (a, b) => a + Math.random() * (b - a);
        const shuffle = (arr) => arr.sort(() => Math.random() - 0.5);
        // Evenly spaced lanes across the viewport, leaving gutters at edges
        const lanesCount = Math.max(glyphs.length, 12);
        const lanes = Array.from({ length: lanesCount }, (_, i) => 10 + (80 * i) / (lanesCount - 1)); // 10vw..90vw
        let laneOrder = shuffle(lanes.slice());

        const assignLane = (el, avoid) => {
          // Rotate through lanes for uniqueness; regenerate when exhausted
          if (!laneOrder.length) laneOrder = shuffle(lanes.slice());
          // Pick a lane not too close to the previous one
          let idx = 0;
          if (typeof avoid === 'number') {
            // Find lane at least ~5vw away
            idx = laneOrder.findIndex((v) => Math.abs(v - avoid) >= 5) || 0;
          }
          const value = laneOrder.splice(idx, 1)[0] || lanes[Math.floor(rand(0, lanes.length))];
          el.style.setProperty('--x', value.toFixed(2) + 'vw');
          el.dataset.lane = String(value);
        };

        const desync = (el) => {
          const cs = getComputedStyle(el);
          const baseDur = parseFloat(cs.animationDuration) || 200; // seconds
          const baseSize = parseFloat(cs.fontSize) || 20;
          // Slight per-cycle variance
          const duration = baseDur * rand(0.92, 1.12);
          el.style.animationDuration = duration.toFixed(2) + 's';
          // Randomize delay a bit to avoid traveling together
          const baseDelay = parseFloat(cs.animationDelay) || 0;
          const jitter = rand(-8, 18); // seconds
          el.style.animationDelay = (baseDelay + jitter).toFixed(2) + 's';
          // Size & opacity
          const scale = rand(GLYPH_MIN, GLYPH_MAX);
          el.style.fontSize = (baseSize * scale).toFixed(2) + 'px';
          el.style.opacity = rand(OP_MIN, OP_MAX).toFixed(2);
        };

        // Initial assignment
        glyphs.forEach((g) => {
          const prev = Number(g.dataset.lane || NaN);
          assignLane(g, prev);
          desync(g);
          g.addEventListener(
            'animationiteration',
            () => {
              const last = Number(g.dataset.lane || NaN);
              assignLane(g, last);
              desync(g);
            },
            { passive: true }
          );
        });
      }
    } catch {}
  });
});